import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, Star, Package, MapPin,
} from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { TRUST_BADGES } from "@/lib/siteData";

const HERO_BG = "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/o53d9j1l_ChatGPT%20Image%20Jul%209%2C%202026%2C%2004_20_57%20PM.png";
const HERO_BG_WEBP = HERO_BG;

export const Hero = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="relative overflow-hidden pt-24 lg:pt-32 pb-12 lg:pb-16 grain isolate">
      {/* Parallax background */}
      <div
        className="absolute inset-0 z-0"
        style={{ transform: `translateY(${scrollY * 0.25}px)` }}
      >
        {/* Phase 2L-A37 — hero v2 ambient background. WebP-first
            with JPG fallback. Loaded eagerly because it's above the
            fold but kept aggressively compressed (~141 KB WebP) and
            sitting behind a 70-85% white wash so visual quality cost
            is dominated by the wash, not the source asset. */}
        <picture>
          <source srcSet={HERO_BG_WEBP} type="image/webp" />
          <img
            src={HERO_BG}
            alt="Aware Water Testing laboratory — technicians analysing tank and bore water samples"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </picture>
        {/* Feb 2026 — full-bleed AWARE lab hero (no mask fade).
            The soft radial spotlight behind the left column (added
            below in the content grid) handles headline legibility on
            desktop; mobile uses the vertical veil below. A very light
            overall veil + a bottom veil keep the trust-badges strip
            reading cleanly. */}
        <div className="absolute inset-0 bg-white/10" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/40" />
        <div className="absolute inset-0 bg-gradient-to-tr from-cyan-50/10 via-transparent to-sky-50/10" />
      </div>

      {/* Floating orbs */}
      <div className="absolute top-32 right-10 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl animate-float z-0 pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-sky-300/20 rounded-full blur-3xl animate-float z-0 pointer-events-none" style={{ animationDelay: "2s" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left: Headline */}
          <div className="lg:col-span-7 relative">
            {/* Feb 2026 — soft radial white spotlight behind the left
                column so H1 / body copy / CTAs sit on a legible bed
                over the busy lab image, without hiding the image
                itself. Desktop-only; mobile relies on the vertical
                white veil layered above the image. */}
            <div
              aria-hidden="true"
              className="hidden lg:block absolute -inset-x-8 -inset-y-6 -z-10 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 80% 70% at 40% 55%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.65) 45%, rgba(255,255,255,0.15) 75%, rgba(255,255,255,0) 100%)",
              }}
            />
            {/* Feb 2026 — brand acronym expansion. Sits above the eyebrow
                chip so first-time visitors immediately learn what AWARE
                stands for. Kept understated (small caps, cyan-700) so it
                reads as a supporting brand line, not a competing headline. */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-3"
              data-testid="hero-aware-tagline"
            >
              <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-cyan-700 leading-snug">
                <span className="text-cyan-900">AWARE</span>
                <span className="mx-1.5 text-slate-400">—</span>
                <span className="text-slate-700">Australian Water Analysis, Reporting &amp; Evaluation</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-700">
                Water Testing Lab · Noosaville · Australia-wide
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-slate-900 leading-[1.05]"
              data-testid="hero-headline"
            >
              Be <span className="water-text">AWARE</span> of What&apos;s in Your Water.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-6 text-lg sm:text-xl text-slate-900 max-w-2xl leading-relaxed hero-legible-text"
              data-testid="hero-supporting-text"
            >
              Professional water testing for tank, bore and household water across Australia.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Link
                to="/book-water-test"
                data-testid={LANDING.heroBookBtn}
                className="btn-aqua group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-base font-semibold"
              >
                Book a Water Test
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <button
                data-testid={LANDING.heroViewTestsBtn}
                onClick={() => document.getElementById("compare")?.scrollIntoView({ behavior: "smooth" })}
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-base font-semibold text-slate-900 bg-white border-2 border-slate-200 hover:border-slate-900 transition-all"
              >
                View Test Options
              </button>
            </motion.div>

            {/* Feb-2026 — "Order a Test Kit" tertiary CTA (Option B mail-in kit).
                Rendered as an understated link + Package icon under the two
                main CTAs so the hero stays clean. Deep-links straight into
                the Booking flow with `?kit=mail_out`, which the Booking
                page consumes on mount to pre-select the postal option. */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-5 flex items-center gap-2 text-sm text-slate-700 hero-legible-text"
            >
              <Package className="w-4 h-4 text-cyan-600 shrink-0" />
              <span>
                Australia-wide?{" "}
                <Link
                  to="/book-water-test?kit=mail_out"
                  data-testid={LANDING.heroOrderKitBtn}
                  className="font-semibold text-cyan-700 hover:text-cyan-800 underline underline-offset-4 decoration-cyan-400/60 hover:decoration-cyan-700 transition-colors inline-flex items-center gap-1"
                >
                  Order a Test Kit
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>{" "}
                <span className="text-slate-600">— we post it, you sample, prepaid return.</span>
              </span>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.55 }}
              className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3"
            >
              {TRUST_BADGES.map((t) => (
                <div key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-cyan-500 flex-shrink-0" />
                  <span className="text-sm text-slate-900 font-medium leading-snug hero-legible-text">{t}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Floating Glass Card
              SCWT-HERO-MAILOUT-KIT · Feb 2026 — DRAFT / preview only.
              Replaces the previous "Sample Report" showcase card with
              a mail-out kit visual so the Australia-wide mail-out
              offering is obvious at first glance. The full sample-
              report walkthrough still lives further down the page in
              `ReportsShowcase`, so nothing is lost — this is a hero
              REBALANCE, not a deletion. Reverting is a one-block
              swap if ops doesn't want it. */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="lg:col-span-5"
          >
            <div className="relative" data-testid="hero-mailout-kit-card">
              <div className="absolute -inset-6 bg-gradient-to-br from-cyan-400/30 to-sky-500/20 rounded-[2.5rem] blur-2xl" />
              <div className="relative glass rounded-3xl p-7 shadow-2xl">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
                      <Package className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-600 font-bold">Now available</div>
                      <div className="text-sm font-semibold text-slate-900">Mail-out testing kits</div>
                    </div>
                  </div>
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-700 text-xs font-bold border border-emerald-500/20"
                    data-testid="hero-mailout-badge-australia"
                  >
                    <MapPin className="w-3 h-3" /> Australia-wide
                  </span>
                </div>

                {/* SCWT-HERO-MAILOUT-KIT-PHOTO · Feb 2026 — real
                    product photo of the AWARE kit (tube + bottle +
                    step-by-step card + prepaid Express Post label +
                    return envelope). Served from /app/frontend/public
                    so we don't depend on the Norton-flagged
                    customer-assets CDN. WebP (110 KB @ 1200×900)
                    with a PNG fallback for older browsers. */}
                <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-white border border-slate-200">
                  <picture>
                    <source srcSet="/mailout-kit-hero.webp" type="image/webp" />
                    <img
                      src="/mailout-kit-hero.webp"
                      alt="AWARE mail-out water testing kit — sample bottle, prepaid Express Post return label, step-by-step instruction card"
                      className="w-full h-auto block"
                      width="1200"
                      height="900"
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      data-testid="hero-mailout-kit-photo"
                    />
                  </picture>
                </div>

                {/* 4-step flow */}
                <ul className="mt-5 space-y-2.5">
                  {[
                    "Book online",
                    "Receive your kit by post",
                    "Return your sample to the lab",
                    "Get clear results and next steps",
                  ].map((step, i) => (
                    <li
                      key={step}
                      className="flex items-center gap-3 py-2 px-3 rounded-lg bg-white/60 border border-white/40"
                    >
                      <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-sm text-slate-800 font-medium">{step}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/book-water-test?kit=mail_out"
                  data-testid="hero-mailout-kit-cta"
                  className="mt-5 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-slate-900 hover:bg-cyan-700 transition text-white text-sm font-semibold"
                >
                  Order a mail-out kit <ArrowRight className="w-4 h-4" />
                </Link>

                <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                    <span className="ml-1 font-semibold text-slate-700">5.0 average</span>
                  </div>
                  <span className="font-medium">Delivered anywhere in Australia</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
